import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, LogOut, ArrowLeft, RefreshCw, Calendar, Send, FileText, Users, UserCheck, UserPlus, Award, AlertTriangle, TrendingUp, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

export default function AdminWeeklyReports() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [latestReport, setLatestReport] = useState(null);
  const [previewReport, setPreviewReport] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      
      if (response.data.user_type !== 'admin') {
        navigate('/login');
        return;
      }
      
      setUser(response.data);
      fetchLatestReport();
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchLatestReport = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/weekly-report/latest`, {
        withCredentials: true
      });
      setLatestReport(response.data);
    } catch (error) {
      // No reports yet is okay
      setLatestReport(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    setShowPreview(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/weekly-report/preview`, {}, {
        withCredentials: true
      });
      setPreviewReport(response.data);
    } catch (error) {
      toast.error('Failed to generate preview');
      setShowPreview(false);
    }
  };

  const handleSendReport = async () => {
    if (!user?.admin_role || user.admin_role !== 'super_admin') {
      toast.error('Only Super Admins can send weekly reports');
      return;
    }
    
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/weekly-report/trigger`, {}, {
        withCredentials: true
      });
      toast.success('Weekly report sent to all Super Admins');
      fetchLatestReport();
      setShowPreview(false);
      setPreviewReport(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send report');
    } finally {
      setSending(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateRange = (start, end) => {
    if (!start || !end) return 'N/A';
    const startDate = new Date(start);
    const endDate = new Date(end);
    return `${startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  const report = showPreview && previewReport ? previewReport : latestReport;
  const summary = report?.summary || {};
  const platformTotals = summary.platform_totals || {};

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="text-slate-600 hover:text-slate-900"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <Building2 className="h-6 w-6 text-orange-500" />
              <h1 className="text-xl font-bold text-slate-900">Weekly Reports</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">{user?.name}</span>
            <button
              onClick={handleLogout}
              className="btn-secondary text-sm flex items-center gap-2"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 mb-8">
          <button
            onClick={handlePreview}
            className="btn-secondary flex items-center gap-2"
            disabled={showPreview && !previewReport}
            data-testid="preview-report-btn"
          >
            <FileText className="h-4 w-4" />
            {showPreview ? 'Previewing...' : 'Preview Current Report'}
          </button>
          
          {user?.admin_role === 'super_admin' && (
            <button
              onClick={handleSendReport}
              className="btn-primary flex items-center gap-2"
              disabled={sending}
              data-testid="send-report-btn"
            >
              <Send className={`h-4 w-4 ${sending ? 'animate-pulse' : ''}`} />
              {sending ? 'Sending...' : 'Send Report Now'}
            </button>
          )}
          
          {showPreview && (
            <button
              onClick={() => { setShowPreview(false); setPreviewReport(null); }}
              className="btn-secondary flex items-center gap-2"
              data-testid="view-latest-btn"
            >
              <Clock className="h-4 w-4" />
              View Last Sent Report
            </button>
          )}
        </div>

        {/* Schedule Info */}
        <div className="card mb-8 bg-blue-50 border-blue-200">
          <div className="flex items-center gap-3">
            <Calendar className="h-5 w-5 text-blue-600" />
            <div>
              <p className="font-medium text-blue-900">Automatic Schedule</p>
              <p className="text-sm text-blue-700">Weekly reports are automatically sent every Monday at 9:00 AM UTC to all Super Admins.</p>
            </div>
          </div>
        </div>

        {/* Report Header */}
        {report ? (
          <>
            <div className="card mb-6">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    {showPreview ? 'Report Preview' : 'Last Sent Report'}
                  </h2>
                  <p className="text-sm text-slate-600 mt-1">
                    Period: {formatDateRange(report.period_start, report.period_end)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500">Report ID</p>
                  <p className="text-sm font-mono text-slate-700">{report.report_id}</p>
                  <p className="text-xs text-slate-500 mt-1">Generated: {formatDate(report.generated_at)}</p>
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {/* New Signups */}
              <div className="card" data-testid="stat-new-trades">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <UserPlus className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">New Tradespeople</p>
                    <p className="text-2xl font-bold text-slate-900">{summary.new_trade_signups || 0}</p>
                  </div>
                </div>
              </div>

              <div className="card" data-testid="stat-new-employers">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <UserPlus className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">New Employers</p>
                    <p className="text-2xl font-bold text-slate-900">{summary.new_employer_signups || 0}</p>
                  </div>
                </div>
              </div>

              {/* Approvals */}
              <div className="card" data-testid="stat-pending">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-yellow-100 rounded-lg">
                    <Clock className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Pending Approvals</p>
                    <p className="text-2xl font-bold text-yellow-600">{summary.pending_approvals || 0}</p>
                  </div>
                </div>
              </div>

              <div className="card" data-testid="stat-approved">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-emerald-100 rounded-lg">
                    <UserCheck className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Approved This Week</p>
                    <p className="text-2xl font-bold text-emerald-600">{summary.approved_this_week || 0}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Referral & Founding */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="card" data-testid="stat-referrals">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Qualified Referrals</p>
                    <p className="text-2xl font-bold text-slate-900">{summary.qualified_referrals || 0}</p>
                  </div>
                </div>
              </div>

              <div className="card" data-testid="stat-eligible">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-indigo-100 rounded-lg">
                    <Award className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Eligible for Founding</p>
                    <p className="text-2xl font-bold text-slate-900">{summary.eligible_for_founding || 0}</p>
                  </div>
                </div>
              </div>

              <div className="card" data-testid="stat-founding-granted">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-amber-100 rounded-lg">
                    <Award className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Founding Granted</p>
                    <p className="text-2xl font-bold text-slate-900">{summary.founding_granted_this_week || 0}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Flagged & Rejected */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="card border-red-200 bg-red-50" data-testid="stat-suspicious">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-red-100 rounded-lg">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-red-700">Suspicious Signups</p>
                    <p className="text-2xl font-bold text-red-600">{summary.suspicious_signups || 0}</p>
                  </div>
                </div>
              </div>

              <div className="card" data-testid="stat-rejected">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-slate-100 rounded-lg">
                    <Users className="h-6 w-6 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Rejected This Week</p>
                    <p className="text-2xl font-bold text-slate-900">{summary.rejected_this_week || 0}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Platform Totals */}
            <div className="card bg-slate-100" data-testid="platform-totals">
              <h3 className="font-semibold text-slate-900 mb-4">Platform Totals</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div>
                  <p className="text-sm text-slate-600">Total Users</p>
                  <p className="text-xl font-bold text-slate-900">{platformTotals.total_users || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Tradespeople</p>
                  <p className="text-xl font-bold text-slate-900">{platformTotals.total_trades || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Employers</p>
                  <p className="text-xl font-bold text-slate-900">{platformTotals.total_employers || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Founding Members</p>
                  <p className="text-xl font-bold text-purple-600">{platformTotals.total_founding_members || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Total Referrals</p>
                  <p className="text-xl font-bold text-slate-900">{platformTotals.total_referrals || 0}</p>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="card text-center py-12">
            <FileText className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Reports Yet</h3>
            <p className="text-slate-600 mb-4">
              No weekly reports have been generated yet. Click "Preview Current Report" to see what the next report will contain.
            </p>
            <button
              onClick={handlePreview}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="first-preview-btn"
            >
              <FileText className="h-4 w-4" />
              Preview Current Report
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
